import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { buildWarehouseLocationColumns } from '#/modules/imsMgmt/settings/storePlace/data/warehouseLocationColumns';
import { warehouseLocationSideMsgBlocks } from '#/modules/imsMgmt/settings/storePlace/data/warehouseLocationSideMsgConfig';
import { getWarehouseLocationApiErrorMessage } from '#/modules/imsMgmt/settings/storePlace/helper/apiError';
import {
  hydrateWarehouseFilterLabels,
} from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import useWarehouseLocationList from '#/modules/imsMgmt/settings/storePlace/hooks/useWarehouseLocationList';

function buildLocationCreatePath(listQuery) {
  const warehouseId = listQuery.filters?.warehouseId;
  const presetId = Array.isArray(warehouseId) ? warehouseId[0] : warehouseId;

  if (!presetId) {
    return ROUTES.storePlaceLocationCreate;
  }

  return `${ROUTES.storePlaceLocationCreate}?warehouseId=${encodeURIComponent(presetId)}`;
}

const WarehouseLocationListPage = () => {
  const listRef = useRef(null);
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.WAREHOUSE_CREATE);
  const canUpdate = useCan(PERM.WAREHOUSE_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useWarehouseLocationList();

  useEffect(() => {
    let cancelled = false;

    hydrateWarehouseFilterLabels(
      listQuery.filters?.warehouseId,
      warehouseLabelCache,
    ).then((didHydrate) => {
      if (!cancelled && didHydrate) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.warehouseId, warehouseLabelCache]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildWarehouseLocationColumns({
        filters: listQuery.filters || {},
        onFilterChange: setFilter,
        warehouseLabelCache,
        canShowEdit: () => canUpdate,
      });
    },
    [listQuery.filters, setFilter, warehouseLabelCache, labelCacheVersion, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const createPath = useMemo(
    () => buildLocationCreatePath(listQuery),
    [listQuery],
  );

  const headerAction = canCreate ? (
    <ListAddButton to={createPath} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">儲位</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.warehouse_location.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError ? getWarehouseLocationApiErrorMessage(error, '載入列表失敗') : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號或名稱"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
            <p className="mt-3 text-sm text-muted-foreground">
              可先以表頭「倉別」篩選，再按新增會帶入預選倉別。
              <Link className="ml-2 text-primary-600 hover:underline" to={ROUTES.storePlaceWarehouse}>
                管理倉別
              </Link>
            </p>
          </div>

          <SideMsg
            title="儲位"
            titleValue="name"
            displayData={displayData}
            blocks={warehouseLocationSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default WarehouseLocationListPage;
